import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Col, Form, Row } from "react-bootstrap";
import { get, updateToken, post, put } from "../../../../../helpers/api_helper";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import { roleSchema } from "../../../../../helpers/yup_validator";
import toast from "react-hot-toast";
import red_cross from "../../../../../Assets/svg/red-cross.svg";

const NewRoleModal = ({ show, onHide, handleRefresh, mode, editRole }) => {
  const token = useSelector((state) => state.login.token);
  const [list, setList] = useState([]);

  const fetchList = useCallback(async () => {
    try {
      const response = await get("/settings/accesslist");
      if (response?.success) {
        setList(response?.data);
      }
    } catch (error) {
      console.log("An error occurred while fetching list", error);
    }
  }, []);

  useEffect(() => {
    if (token) {
      updateToken(token);
      if (mode === "create") {
        fetchList();
      }
    }
    if (mode === "edit" && editRole) {
      setList(editRole.permissions);
    }
  }, [token, fetchList, mode, editRole]);

  const formik = useFormik({
    initialValues: {
      name: editRole?.name || "",
      status: editRole?.status === "active",
      permissions: editRole?.permissions || list || [],
    },
    validationSchema: roleSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload = {
        name: values.name,
        status: values.status ? "active" : "inactive",
        permissions: list?.map((item) => ({
          type: item.type,
          label: item.label,
          permissions: item.permissions,
        })),
      };

      try {
        if (mode === "create") {
          await post("/roles", payload);
          toast.success("Role created successfully");
          formik.resetForm();
        } else if (mode === "edit") {
          await put(`/roles/${editRole._id}`, payload);
          toast.success("Role Updated successfully");
        }
        formik.resetForm();
        onHide();
        typeof handleRefresh == "function" && handleRefresh();
      } catch (error) {
        console.log("An error occurred while creating role", error);
        toast.error("An error occurred while creating role");
      }
    },
  });

  const handleMainCheckboxChange = (index) => {
    const updatedList = [...list];
    const currentPermissions = updatedList[index].permissions;

    const allChecked = currentPermissions.every((perm) => perm.value);
    currentPermissions.forEach((perm) => {
      perm.value = !allChecked;
    });

    if (!allChecked) {
      currentPermissions.find((perm) => perm.label === "VIEW").value = true;
    }

    formik.setFieldValue("permissions", updatedList);
    setList(updatedList);
  };

  const handlePermissionChange = (groupIndex, permIndex) => {
    const updatedList = [...list];
    const permission = updatedList[groupIndex].permissions[permIndex];

    permission.value = !permission.value;

    if (permission.label !== "VIEW" && permission.value) {
      const viewPermission = updatedList[groupIndex].permissions.find(
        (perm) => perm.label === "VIEW"
      );
      viewPermission.value = true;
    }

    formik.setFieldValue("permissions", updatedList);

    setList(updatedList);
  };

  return (
    <Modal
      show={show}
      onHide={() => onHide(false)}
      backdrop="static"
      keyboard={false}
      centered
      scrollable="true"
      className="addUpdateContent"
      size="xl"
    >
      <Modal.Body>
        <div className="topHeader d-flex align-items-start justify-content-between gap-10 pb-2">
          <div className="left">
            <h5 className="m-0 fw-sbold">
              {editRole ? "Edit Role" : "Add New Role"}
            </h5>
          </div>
          <Button
            className="p-0 border-0"
            variant="transparent"
            onClick={() => onHide(false)}
          >
            <img src={red_cross} alt="Close" />
          </Button>
        </div>
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col lg="4" sm="6" className="my-2">
              <label htmlFor="name" className="form-label m-0 text-dark">
                Name
              </label>
              <input
                type="text"
                placeholder="Accounting"
                className="form-control"
                id="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                required
              />
              {formik.touched.name && formik.errors.name ? (
                <div className="text-danger">{formik.errors.name}</div>
              ) : null}
            </Col>
            <Col lg="4" sm="6" className="my-2 align-self-center">
              <div className="d-flex align-items-center justify-content-between">
                <label htmlFor="" className="form-label text-dark m-0">
                  Status
                </label>
                <Form.Check
                  type="switch"
                  id="custom-switch"
                  label=""
                  checked={formik.values.status}
                  onChange={() =>
                    formik.setFieldValue("status", !formik.values.status)
                  }
                />
              </div>
            </Col>
          </Row>
          <Row>
            <label className="form-label text-lg font-weight-bold m-0 text-dark">
              Permissions
            </label>
            {list?.map((item, index) => (
              <Col lg="4" sm="6" className="my-2" key={item.type}>
                <div className="cardCstm p-2 box">
                  <div className="top d-flex align-items-center justify-content-between gap-10 border-bottom px-2">
                    <label className="form-label text-dark m-0">
                      {item.label}
                    </label>
                    <input
                      type="checkbox"
                      className="form-check"
                      onChange={() => handleMainCheckboxChange(index)}
                      checked={item.permissions.every((perm) => perm.value)}
                    />
                  </div>
                  <div className="bodyContent px-2">
                    <ul className="list-unstyled ps-0 mb-0">
                      {item.permissions?.map((perm, permIndex) => (
                        <li
                          className="py-1 d-flex align-items-center justify-content-between gap-10"
                          key={perm.label}
                        >
                          <label className="form-label text-dark m-0">
                            {perm.label}
                          </label>
                          <input
                            type="checkbox"
                            className="form-check"
                            checked={perm.value}
                            onChange={() =>
                              handlePermissionChange(index, permIndex)
                            }
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Col>
            ))}
            {formik.touched.permissions && formik.errors.permissions ? (
              <div className="text-danger">{formik.errors.permissions}</div>
            ) : null}
          </Row>
          <Button
            type="submit"
            className="d-flex align-items-center w-100 justify-content-center commonBtn mt-3"
          >
            {editRole ? "Update Role" : "Create Role"}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default NewRoleModal;
